import { useMemo } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, Button, ModalV2, Text } from "@jsq/ds/core";
import { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { RiskRating } from "~/graphql/supergraphEnums";
import { RISK_RATING_PROPS } from "../constants";
import PositionName from "../PositionName";
import { RiskRatingCell } from "../RiskRatingCell";
import { RiskRatingChangeInfo } from "../types";
import { calculateRiskRatingChange } from "../utils";
import {
  borderStyle,
  ratingCatgory,
  ratingItem,
  ratingItemHeader,
  ratingTableWrapper,
  ratingValue,
} from "./styles_vanilla.css";

interface Props {
  positionData?: {
    accountName: string | undefined;
    entityName: string | undefined;
  } | null;
  riskRatingInfo?: RiskRatingChangeInfo | null;
  onClose: () => void;
}

const RiskRatingChangeDetailsModal = ({ positionData, riskRatingInfo, onClose }: Props) => {
  const riskRatingData = useMemo(() => {
    if (!riskRatingInfo) return [];

    return [
      {
        category: "Bank account domicile",
        currentRating:
          RISK_RATING_PROPS[riskRatingInfo.currentRatingInfo?.bankAccount ?? RiskRating.None],
        proposedRating: {
          rating:
            RISK_RATING_PROPS[
              riskRatingInfo.proposedRatingInfo?.bankAccount ?? RiskRating.None
            ],
          changeIcon: calculateRiskRatingChange(
            riskRatingInfo.currentRatingInfo?.bankAccount,
            riskRatingInfo.proposedRatingInfo?.bankAccount,
          ),
        },
      },
      {
        category: "Payment beneficiary",
        currentRating:
          RISK_RATING_PROPS[riskRatingInfo.currentRatingInfo?.beneficiary ?? RiskRating.None],
        proposedRating: {
          rating:
            RISK_RATING_PROPS[
              riskRatingInfo.proposedRatingInfo?.beneficiary ?? RiskRating.None
            ],
          changeIcon: calculateRiskRatingChange(
            riskRatingInfo.currentRatingInfo?.beneficiary,
            riskRatingInfo.proposedRatingInfo?.beneficiary,
          ),
        },
      },
      {
        category: "Investor",
        currentRating:
          RISK_RATING_PROPS[riskRatingInfo.currentRatingInfo?.investor ?? RiskRating.None],
        proposedRating: {
          rating:
            RISK_RATING_PROPS[riskRatingInfo.proposedRatingInfo?.investor ?? RiskRating.None],
          changeIcon: calculateRiskRatingChange(
            riskRatingInfo.currentRatingInfo?.investor,
            riskRatingInfo.proposedRatingInfo?.investor,
          ),
        },
      },
      {
        category: "Position",
        currentRating: RISK_RATING_PROPS[riskRatingInfo.currentRating ?? RiskRating.None],
        proposedRating: {
          rating: RISK_RATING_PROPS[riskRatingInfo.proposedRating ?? RiskRating.None],
          changeIcon: calculateRiskRatingChange(
            riskRatingInfo.currentRating,
            riskRatingInfo.proposedRating,
          ),
        },
      },
    ];
  }, [riskRatingInfo]);

  return (
    <ModalV2
      show={!!riskRatingInfo}
      onHide={onClose}
      size="large"
      title="Risk rating calculation"
      description={
        <PositionName account={positionData?.accountName} entity={positionData?.entityName} />
      }
    >
      <ModalV2.Body>
        <Flex className={ratingTableWrapper}>
          <Flex className={ratingItemHeader}>
            <div className={ratingCatgory}></div>
            <Text variant="strong-md" className={ratingValue}>
              Current risk rating
            </Text>
            <Text variant="strong-md" className={ratingValue}>
              Proposed risk rating
            </Text>
          </Flex>
          {riskRatingData.map((item, index) => (
            <Flex
              key={item.category}
              className={ratingItem}
              style={assignInlineVars({
                [borderStyle]:
                  index === riskRatingData.length - 1
                    ? `1px solid ${Colors.Neutral800}`
                    : `1px solid ${Colors.Neutral400}`,
              })}
            >
              <Text variant="strong-md" className={ratingCatgory}>
                {item.category}
              </Text>
              <div className={ratingValue}>
                <RiskRatingCell
                  hasIcon={item.currentRating.hasIcon}
                  iconType={item.currentRating.iconType as IconType}
                  color={item.currentRating.color}
                  text={item.currentRating.text}
                  isFullWidth={true}
                />
              </div>
              <div className={ratingValue}>
                <RiskRatingCell
                  hasIcon={item.proposedRating.rating.hasIcon}
                  iconType={item.proposedRating.rating.iconType as IconType}
                  color={item.proposedRating.rating.color}
                  text={item.proposedRating.rating.text}
                  changeIcon={item.proposedRating.changeIcon}
                  isFullWidth={true}
                />
              </div>
            </Flex>
          ))}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end">
          <Button variant="primary" onClick={onClose} data-testid="risk-rating-modal-close">
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default RiskRatingChangeDetailsModal;
